import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { PortfolioInboxes } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { PopulationBasedFundingProposalCount } from "@/Portfolio/Components/PopulationBasedFunding/ProposalCount/PopulationBasedFundingProposalCount";
import { PortfolioFormsMenuSectionRegistryView } from "./Extensions/PortfolioFormsMenuSectionRegistry";

import styles from "./PortfolioTreeControl.module.scss";

interface IPortfolioTreeControlProps {
    notesCount: number;
    apssRecentCount: number;
    currentInbox: PortfolioInboxes;
    supervisionNoteCount?: number;
    failedFaxesCount?: number;
    failedSharedItemsCount?: number;
    apssConseilNumeriqueCount: number;
    apssActionRequiredCount?: number;
}

class PortfolioTreeControl extends React.Component<IPortfolioTreeControlProps, never> {
    public constructor(props: IPortfolioTreeControlProps) {
        super(props);
    }

    public render(): JSX.Element {
        const apssRecentCount = !isNaN(this.props.apssRecentCount)
            ? " (" + this.props.apssRecentCount + ")"
            : "";
        const apssConseilNumeriqueCount = !isNaN(this.props.apssConseilNumeriqueCount) && this.props.apssConseilNumeriqueCount > 0
            ? " (" + this.props.apssConseilNumeriqueCount + ")"
            : "";
        const apssActionRequiredCount = !Number.isNaN(this.props.apssActionRequiredCount) && this.props.apssActionRequiredCount > 0
            ? " (" + this.props.apssActionRequiredCount + ")"
            : "";

        let pendingApprovalsLabel: string;

        if (EmployeeTitleHelper.isStudent(sessionStore.myleEmployeeTitle))
            pendingApprovalsLabel = cacheStore.GetString("SentForApprovalLabel");
        else if (sessionStore.myleEmployeeTitle !== EmployeeTitle.NONE && sessionStore.myleEmployeeTitle !== EmployeeTitle.Unknown)
            pendingApprovalsLabel = cacheStore.GetString("SupervisionLabel");
        else
            pendingApprovalsLabel = null;

        const showPortfolioPortalSyncHistory = cacheStore.ClinicSettings.EnablePortfolioPortalSyncHistory && cacheStore.ClinicSettings_Portal.EnablePortalWebsite;
        const showPortfolioPortalPendingItems = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;

        return (
            <div className={styles.treeControlContainer}>
                <div className={styles.treeMenuContainer} data-cy="portfolio-treeMenu">
                    <div className={styles.treeTitle}>
                        {cacheStore.GetString("ToApproveLabel")}
                    </div>
                    <div className={styles.treeLinksContainer}>
                        <div>
                            <HyperLinkControl
                                IsUnderLined={this.props.currentInbox == PortfolioInboxes.Notes}
                                operation={LocalOperations.Portfolio.SwitchTo_NotesInbox}
                                text={"- " + cacheStore.GetString("MyNotesLabel")}
                            />
                        </div>
                        {!IsNullOrWhiteSpace(pendingApprovalsLabel) && (
                            <div className={styles.linksWithCounters}>
                                <div>
                                    <HyperLinkControl
                                        IsUnderLined={this.props.currentInbox == PortfolioInboxes.PendingApprovals}
                                        operation={LocalOperations.Portfolio.SwitchTo_PendingApprovals}
                                        text={"- " + pendingApprovalsLabel}
                                    />
                                </div>
                                {this.props.supervisionNoteCount > 0 && (
                                    <span className={`${styles.counter} ${this.props.currentInbox == PortfolioInboxes.PendingApprovals ? styles.selected : ""}`}>
                                        ( {this.props.supervisionNoteCount} )
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.treeTitle}>
                        {cacheStore.GetString("FollowUpsLabel")}
                    </div>
                    <div className={styles.treeLinksContainer}>
                        <div>
                            <HyperLinkControl
                                IsUnderLined={this.props.currentInbox == PortfolioInboxes.Requests}
                                operation={LocalOperations.Portfolio.SwitchTo_RequestsInbox}
                                text={`- ${cacheStore.GetString("MyExamRequestsLabel")}`}
                            />
                        </div>
                        <div>
                            <HyperLinkControl
                                IsUnderLined={this.props.currentInbox == PortfolioInboxes.ConsultationRequests}
                                operation={LocalOperations.Portfolio.SwitchTo_ConsultationRequestsInbox}
                                text={`- ${cacheStore.GetString("MyConsultationRequestsLabel")}`}
                            />
                        </div>
                    </div>
                    {cacheStore.ClinicSettings.EnablePreventiveCareDashboard && (
                        <>
                            <div className={styles.treeTitle}>
                                {cacheStore.GetString("PreventiveActivitiesLabel")}
                            </div>
                            <div className={styles.treeLinksContainer}>
                                <div>
                                    <HyperLinkControl
                                        IsUnderLined={this.props.currentInbox == PortfolioInboxes.PreventiveActivities}
                                        operation={LocalOperations.Portfolio.SwitchTo_PreventiveActivitiesInbox}
                                        text={"- " + cacheStore.GetString("MyPreventiveItemsLabel")}
                                    />
                                </div>
                                {cacheStore.ClinicSettings.EnableRecallList && (
                                    <div>
                                        <HyperLinkControl
                                            IsUnderLined={this.props.currentInbox == PortfolioInboxes.PreventiveRecallList}
                                            operation={LocalOperations.Portfolio.SwitchTo_PreventiveRecallList}
                                            text={"- " + cacheStore.GetString("RecallListLabel")}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    <div className={styles.treeTitle}>
                        {cacheStore.GetString("FaxesLabel")}
                    </div>
                    <div className={styles.treeLinksContainer}>
                        <div data-cy="my_sent_faxes">
                            <HyperLinkControl
                                IsUnderLined={this.props.currentInbox == PortfolioInboxes.Faxes}
                                operation={LocalOperations.Portfolio.SwitchTo_SentFaxInbox}
                                text={"- " + cacheStore.GetString("MySentFaxLabel")}
                            />
                        </div>
                        <div className={styles.linksWithCounters}>
                            <div data-cy="failed-faxes-link">
                                <HyperLinkControl
                                    IsUnderLined={this.props.currentInbox == PortfolioInboxes.FailedFaxes}
                                    operation={LocalOperations.Portfolio.SwitchTo_FailedFaxes}
                                    text={"- " + cacheStore.GetString("FailedFaxesLabel")}
                                />
                            </div>

                            {this.props.failedFaxesCount > 0 && (
                                <span className={`${styles.counter} ${this.props.currentInbox == PortfolioInboxes.FailedFaxes ? styles.selected : ""}`}>
                                    ( {this.props.failedFaxesCount} )
                                </span>
                            )}
                        </div>
                    </div>

                    {(showPortfolioPortalSyncHistory || showPortfolioPortalPendingItems) && (
                        <>
                            <div className={styles.treeTitle}>
                                {cacheStore.GetString("PortalLabel")}
                            </div>
                            <div className={styles.treeLinksContainer}>
                                {showPortfolioPortalPendingItems && (
                                    <div>
                                        <HyperLinkControl
                                            IsUnderLined={this.props.currentInbox == PortfolioInboxes.PortalPendingItems}
                                            operation={LocalOperations.Portfolio.SwitchTo_PortalPendingItems}
                                            text={"- " + cacheStore.GetString("PortalPendingItemsLabel")}
                                        />
                                    </div>
                                )}
                                {showPortfolioPortalPendingItems && (
                                    <div className={styles.linksWithCounters}>
                                        <HyperLinkControl
                                            IsUnderLined={this.props.currentInbox == PortfolioInboxes.PortalSyncItemsErrors}
                                            operation={LocalOperations.Portfolio.SwitchTo_PortalPendingErrorItems}
                                            text={"- " + cacheStore.GetString("ItemsInErrorLabel")}
                                        />
                                        {this.props.failedSharedItemsCount > 0 && (
                                            <span className={`${styles.counter} ${this.props.currentInbox == PortfolioInboxes.PortalSyncItemsErrors ? styles.selected : ""}`}>
                                                ( {this.props.failedSharedItemsCount} )
                                            </span>
                                        )}
                                    </div>
                                )}
                                {showPortfolioPortalSyncHistory && (
                                    <div>
                                        <HyperLinkControl
                                            IsUnderLined={this.props.currentInbox == PortfolioInboxes.PortalSyncItems}
                                            operation={LocalOperations.Portfolio.SwitchTo_PortalSyncItems}
                                            text={"- " + cacheStore.GetString("PortalSyncItemsLabel")}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {!IsNullOrWhiteSpace(sessionStore.getCurrentEmployee().getActiveSite().Model.ClinicHistoricalIds) && (
                        <>
                            <div className={styles.treeTitle}>
                                {cacheStore.GetString("CrdsAndConseilNumeriqueLabel")}
                            </div>
                            <div className={styles.treeLinksContainer}>
                                <div>
                                    <HyperLinkControl
                                        IsUnderLined={this.props.currentInbox == PortfolioInboxes.APSS_Recent}
                                        onChange={() =>
                                            sessionStore.dispatch(LocalOperations.Portfolio.SwitchTo_APSS, {
                                                inboxType: PortfolioInboxes.APSS_Recent
                                            })
                                        }
                                        text={"- " + cacheStore.GetString("RequestsLabel") + apssRecentCount}
                                    />
                                </div>
                                <div>
                                    <HyperLinkControl
                                        IsUnderLined={this.props.currentInbox == PortfolioInboxes.APSS_ActionRequired}
                                        onChange={() => {
                                            sessionStore.dispatch(LocalOperations.Portfolio.SwitchTo_APSS, {
                                                inboxType: PortfolioInboxes.APSS_ActionRequired
                                            });
                                        }}
                                        text={"- CRDS - " + cacheStore.GetString("ActionRequiredLabel") + apssActionRequiredCount}
                                        dataCy="apss-urgent-action-tree-control"
                                    />
                                </div>
                                <div>
                                    <HyperLinkControl
                                        IsUnderLined={this.props.currentInbox == PortfolioInboxes.APSS_ConseilNumerique}
                                        onChange={() =>
                                            sessionStore.dispatch(LocalOperations.Portfolio.SwitchTo_APSS, {
                                                inboxType: PortfolioInboxes.APSS_ConseilNumerique
                                            })
                                        }
                                        text={"- " + "Conseil Num. " + apssConseilNumeriqueCount}
                                        dataCy="conseil-numerique-tree-control"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <div className={styles.treeTitle}>
                        {cacheStore.GetString("FormsLabel")}
                    </div>
                    <div className={styles.treeLinksContainer}>
                        <PortfolioFormsMenuSectionRegistryView currentInbox={this.props.currentInbox} />
                    </div>

                    {cacheStore.ClinicSettings.EnablePopulationBasedFunding && (
                        <>
                            <div className={styles.treeTitle} data-cy="population-based-funding">
                                {cacheStore.GetString("PublicBasedFundingAcronymLabel")}
                            </div>
                            <div className={styles.treeLinksContainer}>
                                <HyperLinkControl
                                    IsUnderLined={this.props.currentInbox == PortfolioInboxes.PopulationBasedFunding}
                                    operation={LocalOperations.Portfolio.SwitchTo_PopulationBasedFunding}
                                    text={`- ${cacheStore.GetString("PatientRegisterLabel")}`}
                                />
                                <PopulationBasedFundingProposalCount
                                    underline={this.props.currentInbox == PortfolioInboxes.PopulationBasedFunding}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }
}

export { PortfolioTreeControl };
